import type { ZplRenderRequest, ZplRenderResponse } from './zplRender.worker'

/**
 * The page side of the preview renderer. The worker, and with it the renderer
 * and its 7 MB WASM file, loads only when the first preview is shown, so no
 * other page pays for it. One worker serves every preview after that.
 */
let worker: Worker | null = null
let nextId = 1
const pending = new Map<number, { resolve: (images: string[]) => void; reject: (err: Error) => void }>()

function failAll(err: Error) {
  for (const p of pending.values()) p.reject(err)
  pending.clear()
}

/** Stops a worker that cannot draw any more, so the next preview starts a new one. */
function drop(w: Worker) {
  w.terminate()
  if (worker === w) worker = null
}

function getWorker(): Worker {
  if (worker) return worker
  const w = new Worker(new URL('./zplRender.worker.ts', import.meta.url), { type: 'module' })
  w.onmessage = (e: MessageEvent<ZplRenderResponse>) => {
    const data = e.data
    const p = pending.get(data.id)
    pending.delete(data.id)
    if (!('error' in data)) {
      p?.resolve(data.images)
      return
    }
    p?.reject(new Error(data.error))
    // Every request still waiting was sent to this worker, which will not answer now.
    if (data.fatal) {
      drop(w)
      failAll(new Error(data.error))
    }
  }
  // The worker script failed to load or crashed.
  w.onerror = e => {
    e.preventDefault()
    drop(w)
    failAll(new Error(e.message || 'The preview worker stopped.'))
  }
  worker = w
  return w
}

/**
 * Draws a ZPL job as PNG images (base64), one per ^XA … ^XZ label in it.
 * Sizes are in millimetres; `dpmm` is dots per millimetre.
 */
export function renderZpl(zpl: string, size: { widthMm: number; heightMm: number; dpmm: number }): Promise<string[]> {
  const id = nextId++
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject })
    const request: ZplRenderRequest = { id, zpl, ...size }
    try {
      getWorker().postMessage(request)
    } catch (err) {
      // The worker could not start or take the request: nothing will answer it.
      pending.delete(id)
      throw err
    }
  })
}
